import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useError, useForm } from '../../hooks';
import { useFirebaseCRUD } from '../../hooks/useFirebaseCRUD';

import TextField from '../Form/TextField';
import Columns from '../Columns';
import DropdownMenu, { DropdownItem } from '../DropdownMenu';
import ContextMenu from '../ContextMenu';
import DataTable, { Row, RowData } from '../DataTable';
import Spinner from '../Spinner';

function capitalize(s) {
  return s[0].toUpperCase() + s.slice(1);
}

function UserRoleGrantsList({ organizationId, roleId, grants = [], reloadData }) {
  const { updateDocument } = useFirebaseCRUD();
  const setError = useError();

  async function deleteGrant(grantId) {
    try {
      if (window.confirm('Are you sure you wish to delete this grant?')) {
        // ✅ Remove the grant from Firestore
        const updatedGrants = grants.filter((g) => g.id !== grantId);
        await updateDocument(`organizations/${organizationId}/user-roles/${roleId}`, {
          grants: updatedGrants,
        });

        await reloadData();
      }
    } catch (err) {
      setError(err);
    }
  }

  const sortedGrants = (grants || []).sort((a, b) =>
    a.resource.localeCompare(b.resource)
  );

  return (
    <DataTable
      columns={[
        'ID',
        'Resource',
        'Action',
        'Possession',
        'Attributes',
        'Actions',
      ]}
      className="is-fullwidth"
    >
      {sortedGrants.map((grant) => (
        <Row key={grant.id}>
          <RowData>{grant.id}</RowData>
          <RowData>{grant.resource}</RowData>
          <RowData>{capitalize(grant.action)}</RowData>
          <RowData>{capitalize(grant.possession)}</RowData>
          <RowData>
            {(grant.attributes || []).map((attr) => (
              <div key={attr} className="is-size-7">
                {attr}
              </div>
            ))}
          </RowData>
          <RowData>
            <DropdownMenu label="Actions" small={true}>
              <DropdownItem onClick={() => deleteGrant(grant.id)}>
                Delete
              </DropdownItem>
            </DropdownMenu>
          </RowData>
        </Row>
      ))}
    </DataTable>
  );
}

export default function ({ id, organizationId }) {
  const navigate = useNavigate();
  const setError = useError();
  const { getDocument, updateDocument, deleteDocument } = useFirebaseCRUD();

  const [organization, setOrganization] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const [, formData, formError] = useForm();

  async function loadData() {
    setLoading(true);
    try {
      const org = await getDocument(`organizations/${organizationId}`);
      const role = await getDocument(
        `organizations/${organizationId}/user-roles/${id}`
      );
      setOrganization(org);
      setUserRole(role);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, [organizationId, id]);

  if (loading || !organization || !userRole) {
    return <Spinner />;
  }

  async function handleSubmit(e) {
    e.preventDefault();

    let { ...permissions } = formData;
    permissions = Object.keys(permissions).filter((key) => permissions[key]);

    try {
      await updateDocument(
        `organizations/${organizationId}/user-roles/${id}`,
        {
          permissions,
        }
      );

      navigate(`/organizations/${organization.id}`);
    } catch (err) {
      if (err.message === 'validation_error') {
        formError(err.props);
      } else {
        setError(err);
      }
    }
  }

  async function deleteUserRole() {
    try {
      if (window.confirm('Are you sure you want to delete this role?')) {
        await deleteDocument(
          `organizations/${organizationId}/user-roles/${id}`
        );
      }

      navigate(`/organizations/${organizationId}`);
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div>
      <ContextMenu
        renderTitle={() => (
          <h1 className="title">Editing role "{userRole.name}"</h1>
        )}
      >
        <DropdownMenu label="Actions">
          <DropdownItem
            onClick={() =>
              navigate(
                `/user-role-grants/create?organizationId=${organizationId}&roleId=${id}`
              )
            }
          >
            Add a new grant
          </DropdownItem>
          <DropdownItem
            onClick={() =>
              navigate(
                `/user-roles/copy/${id}?organizationId=${organizationId}`
              )
            }
          >
            Copy role to another organization
          </DropdownItem>
          <DropdownItem onClick={() => deleteUserRole()}>
            Delete role
          </DropdownItem>
        </DropdownMenu>
      </ContextMenu>
      <div className="box">
        <h2 className="subtitle">Details</h2>
        <form onSubmit={handleSubmit}>
          <Columns>
            <TextField
              label="Organization"
              value={organization.name}
              readOnly={true}
            />
            <TextField label="Role Name" value={userRole.name} readOnly={true} />
          </Columns>
        </form>
      </div>
      <div className="box">
        <h2 className="subtitle">Grants</h2>
        <UserRoleGrantsList
          organizationId={organizationId}
          roleId={id}
          grants={userRole.grants || []}
          reloadData={loadData}
        />
      </div>
    </div>
  );
}
